import React, {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";

import { FETCH_FEATURE_FLAGS, FETCH_SETTINGS_DETAILS } from "@/queries";
import { type QueryResult } from "@apollo/client";
import {
  type FetchFeatureFlags,
  type UpdateUserFeatureFlag as UpdateUserFeatureFlagQuery,
  type UpdateUserFeatureFlagVariables,
} from "@north-beam/nb-client-side";
import { UPDATE_FEATURE_FLAG } from "@pages/settings/northbeam-labs";

import { useNorthbeamMutation } from "./use-northbeam-mutation";
import { useNorthbeamQuery } from "./use-northbeam-query";

export type FeatureFlags = Record<string, boolean>;

export type FeatureFlagContextType = Pick<
  QueryResult<FeatureFlags, Record<never, never>>,
  "loading" | "error"
> & {
  readonly featureFlags?: QueryResult<
    FeatureFlags,
    Record<never, never>
  >["data"];
};

const FeatureFlagContext = createContext<FeatureFlagContextType>({
  featureFlags: undefined,
  loading: false,
  error: undefined,
} as FeatureFlagContextType);

export function FeatureFlagsProvider({ children }: PropsWithChildren<unknown>) {
  const { data, loading, error } = useNorthbeamQuery<FetchFeatureFlags>(
    FETCH_FEATURE_FLAGS,
    {},
  );

  return (
    <FeatureFlagContext.Provider
      value={{ loading, error, featureFlags: data?.me.featureFlags }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
}

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider",
    );
  }
  const { featureFlags, loading, error } = context;

  const hasFeatureFlag = useCallback(
    (featureFlagName: string): boolean => {
      if (featureFlags?.[featureFlagName]) {
        return true;
      }

      return false;
    },
    [featureFlags],
  );

  return { featureFlags, loading, error, hasFeatureFlag };
};

/**
 * @deprecated
 * Hook method is now embedded in `useFeatureFlags` hook.
 * */
export const useHasFeatureFlag = () => {
  const { featureFlags } = useFeatureFlags();
  const hasFeatureFlag = useCallback(
    (featureFlagName: string): boolean => {
      if (featureFlags?.[featureFlagName]) {
        return true;
      }

      return false;
    },
    [featureFlags],
  );

  return hasFeatureFlag;
};

export const useUpdateUserFeatureFlag = () => {
  const [updateUserFeatureFlagMutation, result] = useNorthbeamMutation<
    UpdateUserFeatureFlagQuery,
    UpdateUserFeatureFlagVariables
  >(UPDATE_FEATURE_FLAG);

  const updateUserFeatureFlag = async (
    featureFlagName: string,
    isSet: boolean,
    refetch = true,
  ) => {
    const _result = await updateUserFeatureFlagMutation({
      variables: {
        featureFlagName,
        isSet,
      },
      awaitRefetchQueries: true,
      refetchQueries: refetch
        ? [{ query: FETCH_SETTINGS_DETAILS }, { query: FETCH_FEATURE_FLAGS }]
        : [],
    });

    return _result;
  };

  return [updateUserFeatureFlag, result] as const;
};

export function useShowV3SalesPage() {
  const hasFeatureFlag = useHasFeatureFlag();

  return useMemo(
    () => !hasFeatureFlag("doNotShowNewSalesPage"),
    [hasFeatureFlag],
  );
}
